import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'
import moment from 'moment'
import 'moment/locale/vi'
import styles from '../../DashboardV2.module.scss'
import types from '../../../../constants/types'
import { pathKeys } from '../../../../constants'
import homeApi from '../../../../api/homeApi'
import customerApi from '../../../../api/customerApi'
import scenarioApi from '../../../../api/scenarioApi'
import campaignApi from '../../../../api/campaignApi'

function fmt(n) {
  return (n || 0).toLocaleString('vi-VN')
}

/* Helper cho dải màu rank bar HSL */
function rankColor(i, n) {
  const lightness = 30 + (i / Math.max(n - 1, 1)) * 42
  return `hsl(142, 55%, ${lightness}%)`
}

const STATUS_META_CLS = {
  done: styles.statusDone,
  sending: styles.statusSending,
  scheduled: styles.statusScheduled,
  failed: styles.statusFailed,
  paused: styles.statusPaused,
}

const DashboardV2 = ({ dashboard, campaigns, user }) => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const tableRef = useRef(null)

  useEffect(() => {
    moment.locale(i18n.language || 'vi')
    if ((i18n.language || 'vi') === 'vi') {
      moment.updateLocale('vi', {
        relativeTime: {
          future: '%s tới',
          past: '%s trước',
          s: 'vài giây',
          ss: '%d giây',
          m: '1 phút',
          mm: '%d phút',
          h: '1 giờ',
          hh: '%d giờ',
          d: '1 ngày',
          dd: '%d ngày',
          M: '1 tháng',
          MM: '%d tháng',
          y: '1 năm',
          yy: '%d năm'
        }
      })
    }
  }, [i18n.language])

  // Quick range state
  const [quickRangeIdx, setQuickRangeIdx] = useState(1) // 30 ngày
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'))
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'))

  // Customer insight range state
  const [custPeriodIdx, setCustPeriodIdx] = useState(1)
  const [showCustCustom, setShowCustCustom] = useState(false)

  // Ranking tab state
  const [rankState, setRankState] = useState('fanpage')

  // Campaign filter state
  const [campaignFilter, setCampaignFilter] = useState('all')

  // Copy ID tooltip state
  const [copied, setCopied] = useState(false)

  const handleCopyId = (id) => {
    if (!id) return
    navigator.clipboard?.writeText(id)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // State to hold API data
  const [campaignStatsData, setCampaignStatsData] = useState(null)
  const [isCampaignStatsLoading, setIsCampaignStatsLoading] = useState(true)
  const [messageStatsData, setMessageStatsData] = useState(null)
  const [isMessageStatsLoading, setIsMessageStatsLoading] = useState(true)
  const [customerStatsData, setCustomerStatsData] = useState(null)
  const [customerKpiStatsData, setCustomerKpiStatsData] = useState(null)
  const [isCustomerKpiStatsLoading, setIsCustomerKpiStatsLoading] = useState(true)
  const [rankFanpageData, setRankFanpageData] = useState(null)
  const [rankTagBombotData, setRankTagBombotData] = useState(null)
  const [rankTagFacebookData, setRankTagFacebookData] = useState(null)
  const [upcomingScenarios, setUpcomingScenarios] = useState(null)
  const [stoppedCampaigns, setStoppedCampaigns] = useState([])
  const customerStatisticRequests = useRef(new Map())

  const getCustomerStatistic = useCallback((params) => {
    const requestKey = `${params.from}:${params.to}`
    if (!customerStatisticRequests.current.has(requestKey)) {
      const request = customerApi.getCustomersStatistic(params).finally(() => {
        customerStatisticRequests.current.delete(requestKey)
      })
      customerStatisticRequests.current.set(requestKey, request)
    }
    return customerStatisticRequests.current.get(requestKey)
  }, [])

  // Handle Try GenAI action
  const handleTryItNowGenAI = () => {
    window.open(
      types.URL.FACEBOOK +
        `&token=${localStorage.getItem('token') || null}&guidBombotGenAI=guidBombotGenAI`,
      '_blank'
    )
  }

  // Effect to update dateFrom and dateTo when quickRangeIdx changes
  useEffect(() => {
    const to = moment().format('YYYY-MM-DD')
    let from
    if (quickRangeIdx === 0) from = moment().subtract(7, 'days').format('YYYY-MM-DD')
    else if (quickRangeIdx === 1) from = moment().subtract(30, 'days').format('YYYY-MM-DD')
    else if (quickRangeIdx === 2) from = moment().subtract(3, 'months').format('YYYY-MM-DD')
    else return // quickRangeIdx === 3 (custom): không tự đổi date

    setDateFrom(from)
    setDateTo(to)
  }, [quickRangeIdx])

  // Debounced date range — tránh gọi API liên tục khi user gõ date
  const [debouncedFrom, setDebouncedFrom] = useState(dateFrom)
  const [debouncedTo, setDebouncedTo] = useState(dateTo)
  const debounceTimer = useRef(null)
  useEffect(() => {
    clearTimeout(debounceTimer.current)
    if (dateFrom === debouncedFrom && dateTo === debouncedTo) return undefined

    debounceTimer.current = setTimeout(() => {
      setIsCampaignStatsLoading(true)
      setIsMessageStatsLoading(true)
      setIsCustomerKpiStatsLoading(true)
      setDebouncedFrom(dateFrom)
      setDebouncedTo(dateTo)
    }, 400)
    return () => clearTimeout(debounceTimer.current)
  }, [dateFrom, dateTo, debouncedFrom, debouncedTo])

  // Fetch dữ liệu xếp hạng song song
  useEffect(() => {
    const fetchGlobalStats = async () => {
      const params = { from: debouncedFrom, to: debouncedTo }

      const [fanpage, tagB, tagF] = await Promise.all([
        homeApi.getFanpageStatistic({ ...params, page: 0, size: 5, sort: 'totalCus,desc' }).catch(() => null),
        homeApi.getTagBombotStatistic({ ...params, size: 50, pageId: '' }).catch(() => null),
        homeApi.getTagFacebookStatistic({ ...params, page: 0, size: 50, sort: 'desc' }).catch(() => null),
      ])

      if (fanpage) setRankFanpageData(fanpage)
      if (tagB) setRankTagBombotData(tagB)
      if (tagF) setRankTagFacebookData(tagF)
    }
    fetchGlobalStats()
  }, [debouncedFrom, debouncedTo])

  useEffect(() => {
    let isCurrentRequest = true
    const params = { from: debouncedFrom, to: debouncedTo }

    setIsCampaignStatsLoading(true)
    setCampaignStatsData(null)

    homeApi.getCampaignStatistic(params)
      .then((campaign) => {
        if (isCurrentRequest) setCampaignStatsData(campaign)
      })
      .catch(() => {
        if (isCurrentRequest) setCampaignStatsData(null)
      })
      .finally(() => {
        if (isCurrentRequest) setIsCampaignStatsLoading(false)
      })

    return () => {
      isCurrentRequest = false
    }
  }, [debouncedFrom, debouncedTo])

  useEffect(() => {
    let isCurrentRequest = true
    const params = { from: debouncedFrom, to: debouncedTo }

    setIsCustomerKpiStatsLoading(true)
    setCustomerKpiStatsData(null)

    getCustomerStatistic(params)
      .then((customer) => {
        if (isCurrentRequest) setCustomerKpiStatsData(customer)
      })
      .catch(() => {
        if (isCurrentRequest) setCustomerKpiStatsData(null)
      })
      .finally(() => {
        if (isCurrentRequest) setIsCustomerKpiStatsLoading(false)
      })

    return () => {
      isCurrentRequest = false
    }
  }, [debouncedFrom, debouncedTo, getCustomerStatistic])

  useEffect(() => {
    let isCurrentRequest = true
    const params = { from: debouncedFrom, to: debouncedTo }

    setIsMessageStatsLoading(true)
    setMessageStatsData(null)

    homeApi.getMessageStatistic(params)
      .then((message) => {
        if (isCurrentRequest) setMessageStatsData(message)
      })
      .catch(() => {
        if (isCurrentRequest) setMessageStatsData(null)
      })
      .finally(() => {
        if (isCurrentRequest) setIsMessageStatsLoading(false)
      })

    return () => {
      isCurrentRequest = false
    }
  }, [debouncedFrom, debouncedTo])

  // Fetch upcoming/pending scenarios
  useEffect(() => {
    const fetchScenarios = async () => {
      const res = await scenarioApi.get({ status: 'PENDING', page: 0, size: 10, sort: 'scheduledAt,asc' }).catch(() => null)
      if (res) setUpcomingScenarios(res)
    }
    fetchScenarios()
  }, [])

  // Fetch stopped campaigns — gộp cancel + paused vào 1 request duy nhất
  useEffect(() => {
    const fetchStopped = async () => {
      const res = await campaignApi.get({
        page: 0,
        size: 50,
        sort: 'createdAt,desc',
        search: 'status=in=(cancel,paused)',
      }).catch(() => null)
      const list = res?.content ?? (Array.isArray(res) ? res : [])
      setStoppedCampaigns(list)
    }
    fetchStopped()
  }, [])

  // Fetch customer stats — range riêng theo custPeriodIdx, không phụ thuộc date filter global
  useEffect(() => {
    let isCurrentRequest = true
    const to = moment().format('YYYY-MM-DD')
    let from
    if (custPeriodIdx === 0) from = moment().subtract(10, 'years').format('YYYY-MM-DD')
    else if (custPeriodIdx === 1) from = moment().subtract(30, 'days').format('YYYY-MM-DD')
    else if (custPeriodIdx === 2) from = moment().subtract(3, 'months').format('YYYY-MM-DD')
    else if (custPeriodIdx === 3) from = moment().subtract(6, 'months').format('YYYY-MM-DD')
    else if (custPeriodIdx === 4) from = moment().subtract(12, 'months').format('YYYY-MM-DD')
    else from = moment().subtract(30, 'days').format('YYYY-MM-DD')

    setCustomerStatsData(null)

    getCustomerStatistic({ from, to })
      .then((customer) => {
        if (isCurrentRequest) setCustomerStatsData(customer)
      })
      .catch(() => {
        if (isCurrentRequest) setCustomerStatsData(null)
      })

    return () => {
      isCurrentRequest = false
    }
  }, [custPeriodIdx, getCustomerStatistic])

  /* --- Tính toán & Mapping Dữ liệu --- */
  const campaignStats = useMemo(() => {
    const src = campaignStatsData || dashboard?.campaign || {}
    const running = src.totalCampaignRunning ?? 0
    const scheduled = src.totalCampaignPending ?? 0
    const done = src.totalCampaignDone ?? 0
    const cancelled = src.totalCampaignCancel ?? 0
    const paused = src.totalCampaignStop ?? 0
    
    let total = src.totalCampaigns ?? src.totalCampaign
    if (total === undefined || total === null) {
      total = running + scheduled + done + cancelled + paused
    }

    const percent = src.comparePercent ?? 0

    return { total, running, scheduled, done, cancelled, paused, percent }
  }, [campaignStatsData, dashboard])

  const messageStats = useMemo(() => {
    const src = messageStatsData || dashboard?.fanpage || {}
    const success = src.totalSuccess ?? src.totalMessagesSuccess ?? 0
    const failed = src.totalFail ?? src.totalMessagesFail ?? 0
    const notSend = src.totalMessagesNotSend ?? 0
    
    let total = src.totalSent ?? src.totalMessages
    if (total === undefined || total === null) {
      total = success + failed + notSend
    }

    const percent = src.comparePercent ?? 0

    return { success, failed, notSend, total, percent }
  }, [messageStatsData, dashboard])

  const successRate = useMemo(() => {
    if (!messageStats.total) return '0.0'
    return ((messageStats.success / messageStats.total) * 100).toFixed(1)
  }, [messageStats])

  const customerNewStats = useMemo(() => {
    const src = customerStatsData || dashboard?.customer || {}
    const male = src.totalMale ?? src.maleCus ?? 0
    const female = src.totalFemale ?? src.femaleCus ?? 0
    
    let total = src.totalCustomers ?? src.totalCus ?? src.totalCuss
    if (total === undefined || total === null) {
      total = male + female + (src.totalOther ?? 0)
    }
    
    const other = src.totalOther ?? Math.max(0, total - male - female)
    const hasPhone = src.totalHavePhone ?? src.hasPhoneCus ?? 0
    const nonePhone = src.totalNotHavePhone ?? Math.max(0, total - hasPhone)

    const percent = src.comparePercent ?? 0

    return { total, gender: { male, female, other }, phone: { has: hasPhone, none: nonePhone }, percent }
  }, [customerStatsData, dashboard])

  const customerKpiStats = useMemo(() => {
    const src = customerKpiStatsData || dashboard?.customer || {}
    const male = src.totalMale ?? src.maleCus ?? 0
    const female = src.totalFemale ?? src.femaleCus ?? 0
    let total = src.totalCustomers ?? src.totalCus ?? src.totalCuss

    if (total === undefined || total === null) {
      total = male + female + (src.totalOther ?? 0)
    }

    return { total, percent: src.comparePercent ?? 0 }
  }, [customerKpiStatsData, dashboard])

  // Mini trend chart based on global date range
  const trendLabel = useMemo(() => {
    if (quickRangeIdx === 0) return t('dashboard_v2.date_filter.7d')
    if (quickRangeIdx === 1) return t('dashboard_v2.date_filter.30d')
    if (quickRangeIdx === 2) return t('dashboard_v2.date_filter.3m')
    
    const diff = moment(dateTo).diff(moment(dateFrom), 'days')
    return `${diff} ${t('dashboard_v2.date_filter.7d').replace('7 ', '')}`
  }, [quickRangeIdx, dateFrom, dateTo, t])

  const trendData = useMemo(() => {
    const isWeeklyGroup = quickRangeIdx === 1 || quickRangeIdx === 2
    const validFrom = moment(dateFrom).isValid() ? moment(dateFrom) : moment().subtract(30, 'days')
    const validTo = moment(dateTo).isValid() ? moment(dateTo) : moment()
    const start = validFrom.isAfter(validTo) ? validTo.clone().startOf('day') : validFrom.clone().startOf('day')
    const end = validFrom.isAfter(validTo) ? validFrom.clone().endOf('day') : validTo.clone().endOf('day')

    const series = messageStatsData?.totalSentMessageByDate
    const hasSeries = Array.isArray(series) && series.length > 0

    if (isWeeklyGroup) {
      const dailyMap = {}
      if (hasSeries) {
        series.forEach((item) => {
          if (!item?.date || !moment(item.date).isValid()) return
          const key = moment(item.date).format('YYYY-MM-DD')
          const raw = Number(item.totalSentMessage)
          dailyMap[key] = (dailyMap[key] || 0) + (Number.isFinite(raw) ? raw : 0)
        })
      }

      let wStart = start.clone()
      let weekIdx = 1
      const weekly = []

      while (wStart.isSameOrBefore(end, 'day')) {
        const wEnd = moment.min(wStart.clone().add(6, 'days'), end.clone())
        let totalVal = 0

        if (hasSeries) {
          const dCursor = wStart.clone()
          while (dCursor.isSameOrBefore(wEnd, 'day')) {
            const k = dCursor.format('YYYY-MM-DD')
            totalVal += dailyMap[k] || 0
            dCursor.add(1, 'days')
          }
        }

        const weekLbl = t('dashboard_v2.activity.week_n', { n: weekIdx, defaultValue: `Tuần ${weekIdx}` })
        weekly.push({
          d: weekLbl,
          tooltip: `${weekLbl} (${wStart.format('DD/MM')} - ${wEnd.format('DD/MM')})`,
          v: totalVal,
        })

        wStart = wEnd.clone().add(1, 'days')
        weekIdx++
      }

      return weekly
    }

    // Các option khác (7 ngày, tùy chọn): giữ nguyên theo ngày
    if (hasSeries) {
      const mapped = [...series]
        .filter((item) => moment(item?.date).isValid())
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
        .map((item) => {
          const raw = Number(item.totalSentMessage)
          const dStr = moment(item.date).format('DD/MM')
          return {
            d: dStr,
            tooltip: dStr,
            v: Number.isFinite(raw) ? raw : 0,
          }
        })
      if (mapped.length > 0) return mapped
    }

    const diff = end.diff(start, 'days')
    const days = Number.isFinite(diff) ? Math.max(1, diff + 1) : 7
    const emptySeries = []
    for (let i = days - 1; i >= 0; i--) {
      const dMoment = end.clone().subtract(i, 'days')
      const dStr = dMoment.format('DD/MM')
      emptySeries.push({
        d: dStr,
        tooltip: dStr,
        v: 0,
      })
    }
    return emptySeries
  }, [messageStatsData, dateFrom, dateTo, quickRangeIdx, t])

  const maxTrend = useMemo(() => {
    return Math.max(...trendData.map((t) => t.v), 1)
  }, [trendData])

  const trendTotal = useMemo(() => {
    return trendData.reduce((acc, cur) => acc + cur.v, 0)
  }, [trendData])

  const isGlobalDateChanging = dateFrom !== debouncedFrom || dateTo !== debouncedTo
  const isCampaignChartLoading = isCampaignStatsLoading || isGlobalDateChanging
  const isMessageChartLoading = isMessageStatsLoading || isGlobalDateChanging
  const isCustomerKpiCardLoading = isCustomerKpiStatsLoading || isGlobalDateChanging

  // Ranking data mapping
  const rankData = useMemo(() => {
    // Helper to group by tagName, sum val, sort desc, and take top 5
    const processTags = (apiData, fallbackData) => {
      if (!apiData) return fallbackData || []
      const map = {}
      apiData.forEach(item => {
        if (!item.tagName || item.totalCus === undefined) return
        map[item.tagName] = (map[item.tagName] || 0) + item.totalCus
      })
      const arr = Object.keys(map).map(label => ({ label, val: map[label] }))
      return arr.sort((a, b) => b.val - a.val).slice(0, 5)
    }

    let fanpageArr = rankFanpageData?.content?.map(f => ({ label: f.name, val: f.totalCus })) || dashboard?.rankFanpage || []
    fanpageArr = [...fanpageArr].sort((a, b) => b.val - a.val).slice(0, 5)

    return {
      fanpage: fanpageArr,
      tagBombot: processTags(rankTagBombotData, dashboard?.rankTagBombot),
      tagFacebook: processTags(rankTagFacebookData, dashboard?.rankTagFacebook),
    }
  }, [rankFanpageData, rankTagBombotData, rankTagFacebookData, dashboard])

  // Campaign list mapping — merge regular campaigns + stopped campaigns
  const campaignList = useMemo(() => {
    const mapItem = (item) => {
      let statusKey = 'done'
      if (item.status === 'RUNNING' || item.status === 'SENDING' || item.status === 'running' || item.status === 'sending') statusKey = 'sending'
      else if (item.status === 'PENDING' || item.status === 'SCHEDULED' || item.status === 'scheduled' || item.status === 'init' || item.status === 'pending') statusKey = 'scheduled'
      else if (item.status === 'FAIL' || item.status === 'failed') statusKey = 'failed'
      else if (item.status === 'STOP' || item.status === 'PAUSED' || item.status === 'paused' || item.status === 'cancel' || item.status === 'CANCEL') statusKey = 'paused'
      return {
        code: item.code || item.id?.substring(0, 6) || 'N/A',
        name: item.name || item.title || '',
        fanpage: item.fanpageName || item.pageName || 'Fanpage',
        status: statusKey,
        time: item.createdAt || new Date().toISOString(),
        rel: item.createdAt ? moment(item.createdAt).fromNow() : t('dashboard_v2.activity.just_now'),
      }
    }

    const regularList = (campaigns && Array.isArray(campaigns.content || campaigns))
      ? (campaigns.content || campaigns).map(mapItem)
      : []

    const stoppedList = stoppedCampaigns.map(mapItem)

    // Merge: add stopped items not already in regularList
    const existingCodes = new Set(regularList.map((c) => c.code))
    const mergedStopped = stoppedList.filter((c) => !existingCodes.has(c.code))

    return [...regularList, ...mergedStopped].sort((a, b) =>
      moment(b.time).valueOf() - moment(a.time).valueOf()
    )
  }, [campaigns, stoppedCampaigns])

  const filteredCampaigns = useMemo(() => {
    const list = campaignFilter === 'all'
      ? campaignList
      : campaignList.filter((c) => c.status === campaignFilter)
    return list.slice(0, 5)
  }, [campaignList, campaignFilter])

  const jumpToFailed = () => {
    setCampaignFilter('failed')
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  /* Donut Conic Gradient helpers */
  const donutCampaignStops = useMemo(() => {
    const segs = [
      { val: campaignStats.done, color: '#22C55E' },
      { val: campaignStats.scheduled, color: '#F59E0B' },
      { val: campaignStats.running, color: '#3B82F6' },
      { val: campaignStats.cancelled, color: '#9CA3AF' },
    ]
    const tot = segs.reduce((a, b) => a + b.val, 0) || 1
    let acc = 0
    return segs.map((s) => {
      const start = (acc / tot) * 100
      acc += s.val
      const end = (acc / tot) * 100
      return `${s.color} ${start}% ${end}%`
    }).join(', ')
  }, [campaignStats])

  const donutMessageStops = useMemo(() => {
    const segs = [
      { val: messageStats.success, color: '#22C55E' },
      { val: messageStats.failed, color: '#EF4444' },
      { val: messageStats.notSend, color: '#9CA3AF' },
    ]
    const tot = segs.reduce((a, b) => a + b.val, 0) || 1
    let acc = 0
    return segs.map((s) => {
      const start = (acc / tot) * 100
      acc += s.val
      const end = (acc / tot) * 100
      return `${s.color} ${start}% ${end}%`
    }).join(', ')
  }, [messageStats])

  const donutGenderStops = useMemo(() => {
    const segs = [
      { val: customerNewStats.gender.male, color: '#22C55E' },
      { val: customerNewStats.gender.female, color: '#86EFAC' },
      { val: customerNewStats.gender.other, color: '#D1D5DB' },
    ]
    const tot = segs.reduce((a, b) => a + b.val, 0) || 1
    let acc = 0
    return segs.map((s) => {
      const start = (acc / tot) * 100
      acc += s.val
      const end = (acc / tot) * 100
      return `${s.color} ${start}% ${end}%`
    }).join(', ')
  }, [customerNewStats])

  const donutPhoneStops = useMemo(() => {
    const segs = [
      { val: customerNewStats.phone.has, color: '#22C55E' },
      { val: customerNewStats.phone.none, color: '#D1D5DB' },
    ]
    const tot = segs.reduce((a, b) => a + b.val, 0) || 1
    let acc = 0
    return segs.map((s) => {
      const start = (acc / tot) * 100
      acc += s.val
      const end = (acc / tot) * 100
      return `${s.color} ${start}% ${end}%`
    }).join(', ')
  }, [customerNewStats])

  const userNameDisplay = user?.name || dashboard?.info?.name || t('update.all')

  const renderKpiDelta = (percent) => {
    if (percent === undefined || percent === null) return null;
    const isUp = percent > 0;
    const isDown = percent < 0;
    const isFlat = percent === 0;
    const absVal = Math.abs(percent).toFixed(1).replace(/\.0$/, '');

    return (
      <span className={`${styles.kpiDelta} ${isUp ? styles.up : isDown ? styles.down : styles.flat}`}>
        {isUp && (
          <svg viewBox="0 0 10 10" fill="none">
            <path d="M5 8V2M2 5l3-3 3 3" stroke="currentColor" strokeWidth="2" />
          </svg>
        )}
        {isDown && (
          <svg viewBox="0 0 10 10" fill="none">
            <path d="M5 2v6M2 5l3 3 3-3" stroke="currentColor" strokeWidth="2" />
          </svg>
        )}
        {isFlat && (
          <svg viewBox="0 0 10 10" fill="none">
            <path d="M2 5h6" stroke="currentColor" strokeWidth="2" />
          </svg>
        )}
        {isUp ? '+' : isDown ? '-' : ''}{absVal}%
      </span>
    );
  };

  const renderStatsLoading = () => (
    <div className={styles.statsLoading}>
      <CircularProgress size={24} color="inherit" />
      <span>{t('noti.loading')}</span>
    </div>
  )

  return (
    <div className={styles.container}>
      {/* ===== PAGE HEAD ===== */}
      <div className={styles.pageHead}>
        <div>
          <div className={styles.pageTitle}>{t('dashboard_v2.greeting', { name: userNameDisplay })}</div>
          <div className={styles.pageSub}>
            {t('dashboard_v2.sub')}
          </div>
        </div>
        <div className={styles.dateFilter}>
          <div className={styles.dateChipGroup}>
            {[
              t('dashboard_v2.date_filter.7d'),
              t('dashboard_v2.date_filter.30d'),
              t('dashboard_v2.date_filter.3m'),
              t('dashboard_v2.date_filter.custom'),
            ].map((opt, idx) => (
              <div
                key={idx}
                className={`${styles.dateChip} ${quickRangeIdx === idx ? styles.on : ''}`}
                onClick={() => setQuickRangeIdx(idx)}
              >
                {opt}
              </div>
            ))}
          </div>
          <div className={styles.dateRange}>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value)
                setQuickRangeIdx(3)
              }}
            />
            <span className={styles.dateRangeSep}>{t('dashboard_v2.date_filter.to')}</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value)
                setQuickRangeIdx(3)
              }}
            />
          </div>
        </div>
      </div>

      {/* ===== KPI HERO ROW ===== */}
      <div className={styles.kpiRow}>
        <div className={styles.kpiCard}>
          {isCampaignChartLoading ? renderStatsLoading() : (
            <>
              <div className={styles.kpiTop}>
                <div className={`${styles.kpiIcon} ${styles.iTotal}`}>
                  <svg viewBox="0 0 16 16" fill="none" strokeWidth="2">
                    <circle cx="8" cy="8" r="6" stroke="currentColor" />
                    <path d="M8 5v3l2 1.5" stroke="currentColor" />
                  </svg>
                </div>
                {renderKpiDelta(campaignStats.percent)}
              </div>
              <div className={styles.kpiVal}>{fmt(campaignStats.total)}</div>
              <div className={styles.kpiLbl}>{t('dashboard_v2.kpi.total_campaign')}</div>
            </>
          )}
        </div>

        <div className={styles.kpiCard}>
          {isMessageChartLoading ? renderStatsLoading() : (
            <>
              <div className={styles.kpiTop}>
                <div className={`${styles.kpiIcon} ${styles.iMsg}`}>
                  <svg viewBox="0 0 16 16" fill="none" strokeWidth="2">
                    <path d="M2 8l12-6-4 12-3-5-5-1z" stroke="currentColor" />
                  </svg>
                </div>
                {renderKpiDelta(messageStats.percent)}
              </div>
              <div className={styles.kpiVal}>{fmt(messageStats.total)}</div>
              <div className={styles.kpiLbl}>{t('dashboard_v2.kpi.messages_sent')} · {t('dashboard_v2.kpi.success_rate', { rate: successRate })}</div>
            </>
          )}
        </div>

        <div className={styles.kpiCard}>
          {isCustomerKpiCardLoading ? renderStatsLoading() : (
            <>
              <div className={styles.kpiTop}>
                <div className={`${styles.kpiIcon} ${styles.iCust}`}>
                  <svg viewBox="0 0 16 16" fill="none" strokeWidth="2">
                    <circle cx="6" cy="6" r="2.3" stroke="currentColor" />
                    <path d="M2 13.5c0-2.2 1.8-3.8 4-3.8s4 1.6 4 3.8" stroke="currentColor" />
                    <path d="M10.2 2.4a2.3 2.3 0 010 4.5M13.5 13.5c0-1.9-1.1-3.3-2.6-3.8" stroke="currentColor" />
                  </svg>
                </div>
                {renderKpiDelta(customerKpiStats.percent)}
              </div>
              <div className={styles.kpiVal}>{fmt(customerKpiStats.total)}</div>
              <div className={styles.kpiLbl}>{t('dashboard_v2.kpi.new_customers')}</div>
            </>
          )}
        </div>

        <div
          className={`${styles.kpiCard} ${styles.kpiAlert}`}
          onClick={() => {
            setCampaignFilter('paused')
            if (tableRef.current) tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
        >
          {isCampaignChartLoading ? renderStatsLoading() : (
            <>
              <div className={styles.kpiTop}>
                <div className={`${styles.kpiIcon} ${styles.iWarn}`}>
                  <svg viewBox="0 0 16 16" fill="none" strokeWidth="2">
                    <path d="M8 1.5L1.5 13h13L8 1.5z" stroke="currentColor" />
                    <path d="M8 6.5v3M8 11.5h.01" stroke="currentColor" />
                  </svg>
                </div>
                <span className={`${styles.kpiDelta} ${styles.down}`}>{t('dashboard_v2.kpi.stopped_campaigns')}</span>
              </div>
              <div className={styles.kpiVal}>{campaignStats.cancelled}</div>
              <div className={styles.kpiLbl}>{t('dashboard_v2.kpi.stopped_campaigns')}</div>
            </>
          )}
        </div>

      </div>

      {/* ===== SECTION 1: HOẠT ĐỘNG (2/3) + TÀI KHOẢN (1/3) ===== */}
      <div className={`${styles.section} ${styles.grid21}`}>
        {/* HOẠT ĐỘNG GỬI TIN */}
        <div className={styles.card}>
          <div className={styles.cardHd}>
            <div>
              <div className={styles.cardHdTitle}>{t('dashboard_v2.activity.title')}</div>
              <div className={styles.cardHdSub}>{t('dashboard_v2.activity.sub')}</div>
            </div>
            <Link to={pathKeys.CAMPAIGN} className={styles.linkMore}>
              {t('dashboard_v2.activity.manage')}
              <svg viewBox="0 0 12 12" fill="none"><path d="M4.5 2.5l4 3.5-4 3.5" /></svg>
            </Link>
          </div>

          <div className={styles.cardBd}>
            <div className={styles.donutPair}>
              {/* Donut Campaign */}
              <div className={styles.donutPairItem}>
                {isCampaignChartLoading ? renderStatsLoading() : (
                  <>
                    <div className={styles.donutPairTitle}>{campaignStats.total} {t('dashboard_v2.activity.campaigns')}</div>
                    <div className={styles.donutBlock}>
                      <div className={styles.donutRing} style={{ background: `conic-gradient(${donutCampaignStops})` }}>
                        <div className={styles.donutCenter}>
                          <b>{campaignStats.total}</b>
                          <span>{t('dashboard_v2.activity.campaigns')}</span>
                        </div>
                      </div>
                      <div className={styles.donutLegend}>
                        <div className={styles.legRow}>
                          <span className={styles.legDot} style={{ background: '#22C55E' }} />
                          <span className={styles.legLbl}>{t('dashboard_v2.activity.completed')}</span>
                          <span className={styles.legVal}>{fmt(campaignStats.done)}</span>
                        </div>
                        <div className={styles.legRow}>
                          <span className={styles.legDot} style={{ background: '#F59E0B' }} />
                          <span className={styles.legLbl}>{t('dashboard_v2.activity.scheduled')}</span>
                          <span className={styles.legVal}>{fmt(campaignStats.scheduled)}</span>
                        </div>
                        <div className={styles.legRow}>
                          <span className={styles.legDot} style={{ background: '#3B82F6' }} />
                          <span className={styles.legLbl}>{t('dashboard_v2.activity.running')}</span>
                          <span className={styles.legVal}>{fmt(campaignStats.running)}</span>
                        </div>
                        <div className={styles.legRow}>
                          <span className={styles.legDot} style={{ background: '#9CA3AF' }} />
                          <span className={styles.legLbl}>{t('dashboard_v2.activity.stopped')}</span>
                          <span className={styles.legVal}>{fmt(campaignStats.cancelled)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Donut Message */}
              <div className={styles.donutPairItem}>
                {isMessageChartLoading ? renderStatsLoading() : (
                  <>
                    <div className={styles.donutPairTitle}>{fmt(messageStats.total)} {t('dashboard_v2.activity.messages')}</div>
                    <div className={styles.donutBlock}>
                      <div className={styles.donutRing} style={{ background: `conic-gradient(${donutMessageStops})` }}>
                        <div className={styles.donutCenter}>
                          <b>{fmt(messageStats.total)}</b>
                          <span>{t('dashboard_v2.activity.messages')}</span>
                        </div>
                      </div>
                      <div className={styles.donutLegend}>
                        <div className={styles.legRow}>
                          <span className={styles.legDot} style={{ background: '#22C55E' }} />
                          <span className={styles.legLbl}>{t('dashboard_v2.activity.success')}</span>
                          <span className={styles.legVal}>{fmt(messageStats.success)}</span>
                        </div>
                        <div className={styles.legRow}>
                          <span className={styles.legDot} style={{ background: '#EF4444' }} />
                          <span className={styles.legLbl}>{t('dashboard_v2.activity.failed')}</span>
                          <span className={styles.legVal}>{fmt(messageStats.failed)}</span>
                        </div>
                        <div className={styles.legRow}>
                          <span className={styles.legDot} style={{ background: '#9CA3AF' }} />
                          <span className={styles.legLbl}>{t('dashboard_v2.activity.notsend', 'Chưa gửi')}</span>
                          <span className={styles.legVal}>{fmt(messageStats.notSend)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Trend mini chart */}
            <div className={styles.trendMini}>
              <div className={styles.trendHd}>
                <span>{t('dashboard_v2.activity.trend_title')} · {trendLabel}</span>
                {!isMessageChartLoading && <b>{fmt(trendTotal)} {t('dashboard_v2.activity.messages')}</b>}
              </div>
              <div className={styles.trendBars}>
                {isMessageChartLoading ? (
                  <div className={styles.trendLoading}>
                    <CircularProgress size={24} color="inherit" />
                    <span>{t('noti.loading')}</span>
                  </div>
                ) : trendData.map((bar, i) => {
                  // Sqrt scale: 0 stays 0; small counts stay shorter than a large peak
                  const h = bar.v <= 0
                    ? 0
                    : Math.max(6, Math.round(Math.sqrt(bar.v / maxTrend) * 100))
                  const isPeak = bar.v === maxTrend && bar.v > 0
                  const tip = `${bar.tooltip || bar.d}: ${fmt(bar.v)} ${t('dashboard_v2.activity.messages')}`
                  return (
                    <div key={i} className={styles.trendCol} title={tip}>
                      <div
                        className={`${styles.trendBar} ${isPeak ? styles.peak : ''}`}
                        style={{ height: `${h}%` }}
                        title={tip}
                      />
                      <span className={styles.trendLbl}>{bar.d}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* THÔNG TIN TÀI KHOẢN */}
        <div className={styles.stack}>
          <div className={styles.card}>
            <div className={styles.cardHd}>
              <div className={styles.cardHdTitle}>{t('dashboard_v2.account_info.title')}</div>
            </div>
            <div className={styles.cardBd}>
              <div className={styles.accList}>
                <div className={styles.accRow}>
                  <span className={styles.accK}>{t('dashboard_v2.account_info.id')}</span>
                  <span className={styles.accV}>
                    <span className={styles.accMono}>{dashboard?.info?.id || user?.id || '4445049732178001'}</span>
                    <button className={styles.copyBtn} title={copied ? t('noti.invite_account') : t('common.btn.copy')} onClick={() => handleCopyId(dashboard?.info?.id || user?.id || '4445049732178001')}>
                      <svg viewBox="0 0 12 12" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" /><path d="M2 8V2h6" stroke="currentColor" /></svg>
                    </button>
                  </span>
                </div>
                <div className={styles.accRow}>
                  <span className={styles.accK}>{t('dashboard_v2.account_info.points')}</span>
                  <span className={styles.accV}>{dashboard?.info?.giftPoint ?? 183}</span>
                </div>
                <div className={styles.accRow}>
                  <span className={styles.accK}>{t('dashboard_v2.account_info.expire')}</span>
                  <span className={`${styles.accV} ${styles.lifetime}`}>
                    {dashboard?.info?.expiredAt
                      ? dashboard.info.expiredAt > '2100-12-24'
                        ? t('dashboard_v2.account_info.lifetime')
                        : moment(dashboard.info.expiredAt).format('DD/MM/YYYY')
                      : t('dashboard_v2.account_info.lifetime')}
                  </span>
                </div>
                <div className={styles.accRow}>
                  <span className={styles.accK}>{t('dashboard_v2.account_info.linked')}</span>
                  <span className={styles.accV}>{dashboard?.info?.numRef ?? 2}</span>
                </div>
                <div className={styles.accRow}>
                  <span className={styles.accK}>{t('dashboard_v2.account_info.customers')}</span>
                  <span className={styles.accV}>{fmt(dashboard?.customer?.totalCus ?? 11983)}</span>
                </div>
                <div className={styles.accRow}>
                  <span className={styles.accK}>{t('dashboard_v2.account_info.fanpages')}</span>
                  <span className={styles.accV}>{dashboard?.fanpage?.totalPage ?? 57}</span>
                </div>
              </div>

              <Link to={pathKeys.SETTING_ACCOUNT} className={styles.btnOutline}>
                {t('dashboard_v2.account_info.manage')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ===== SECTION 2: GENAI BANNER ===== */}
      <div className={`${styles.section} ${styles.genaiBanner}`}>
        <div className={styles.genaiBannerLeft}>
          <div className={styles.genaiBadgeLg}>{t('dashboard_v2.genai.badge')}</div>
          <div className={styles.genaiBannerTitle}>
            {t('dashboard_v2.genai.title')}
          </div>
          <div className={styles.genaiStats}>
            <div className={styles.genaiStatItem}>
              <span className={styles.genaiStatVal}>136</span>
              <span className={styles.genaiStatLbl}>{t('dashboard_v2.genai.stat1_lbl')}</span>
            </div>
            <div className={styles.genaiStatSep} />
            <div className={styles.genaiStatItem}>
              <span className={styles.genaiStatVal}>56</span>
              <span className={styles.genaiStatLbl}>{t('dashboard_v2.genai.stat2_lbl')}</span>
            </div>
          </div>
        </div>
        <button className={styles.btnGenaiLg} onClick={handleTryItNowGenAI}>
          {t('dashboard_v2.genai.cta')}
          <svg viewBox="0 0 14 14" fill="none">
            <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="2.4" />
          </svg>
        </button>
      </div>

      {/* ===== SECTION 3: KHÁCH HÀNG MỚI (INSIGHT) ===== */}
      <div className={`${styles.section} ${styles.card}`}>
        <div className={styles.cardHd}>
          <div>
            <div className={styles.cardHdTitle}>{t('dashboard_v2.new_customers.title')}</div>
            <div className={styles.cardHdSub}>{t('dashboard_v2.new_customers.sub')}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <div className={styles.segGroup}>
              {[
                t('update.all'),
                t('dashboard_v2.date_filter.30d'),
                t('dashboard_v2.date_filter.3m'),
                t('dashboard_v2.new_customers.period_6m'),
                t('dashboard_v2.new_customers.period_12m'),
                t('dashboard_v2.new_customers.period_custom'),
              ].map((o, i) => (
                <div
                  key={i}
                  className={`${styles.segBtn} ${custPeriodIdx === i ? styles.active : ''}`}
                  onClick={() => {
                    setCustPeriodIdx(i)
                    setShowCustCustom(i === 5)
                  }}
                >
                  {o}
                </div>
              ))}
            </div>
            {showCustCustom && (
              <div className={styles.dateRange}>
                <input type="date" defaultValue="2025-07-12" />
                <span className={styles.dateRangeSep}>{t('dashboard_v2.date_filter.to')}</span>
                <input type="date" defaultValue="2026-07-11" />
              </div>
            )}
          </div>
        </div>

        <div className={styles.cardBd}>
          <div className={styles.custTotalRow}>
            <div>
              <div className={styles.custTotalVal}>{fmt(customerNewStats.total)}</div>
              <div className={styles.custTotalLbl}>{t('dashboard_v2.new_customers.in_period')}</div>
            </div>
            {(() => {
              const percent = customerNewStats.percent || 0;
              const isUp = percent > 0;
              const isDown = percent < 0;
              const isFlat = percent === 0;
              const absVal = Math.abs(percent).toFixed(1).replace(/\.0$/, '');
              
              let text = t('dashboard_v2.new_customers.vs_prev', { rate: absVal });
              if (isDown) text = text.replace(/^\+/, '-');
              else if (isFlat) text = text.replace(/^\+/, '');

              return (
                <span className={`${styles.kpiDelta} ${isUp ? styles.up : isDown ? styles.down : styles.flat} ${styles.custTotalDelta}`}>
                  {isUp && (
                    <svg viewBox="0 0 10 10" fill="none">
                      <path d="M5 8V2M2 5l3-3 3 3" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  )}
                  {isDown && (
                    <svg viewBox="0 0 10 10" fill="none">
                      <path d="M5 2v6M2 5l3 3 3-3" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  )}
                  {isFlat && (
                    <svg viewBox="0 0 10 10" fill="none">
                      <path d="M2 5h6" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  )}
                  {text}
                </span>
              );
            })()}
          </div>

          <div className={styles.custSplit}>
            <div className={styles.custSplitItem}>
              <div className={styles.custSplitHd}>{t('dashboard_v2.new_customers.gender')}</div>
              <div className={styles.donutBlock}>
                <div
                  className={styles.donutRing}
                  style={{ background: `conic-gradient(${donutGenderStops})` }}
                >
                  <div className={styles.donutCenter}>
                    <b>{Math.round((customerNewStats.gender.male / (customerNewStats.total || 1)) * 100)}%</b>
                    <span>{t('dashboard_v2.new_customers.male')}</span>
                  </div>
                </div>
                <div className={styles.donutLegend}>
                  <div className={styles.legRow}>
                    <span className={styles.legDot} style={{ background: '#22C55E' }} />
                    <span className={styles.legLbl}>{t('dashboard_v2.new_customers.male')}</span>
                    <span className={styles.legVal}>{fmt(customerNewStats.gender.male)}</span>
                  </div>
                  <div className={styles.legRow}>
                    <span className={styles.legDot} style={{ background: '#86EFAC' }} />
                    <span className={styles.legLbl}>{t('dashboard_v2.new_customers.female')}</span>
                    <span className={styles.legVal}>{fmt(customerNewStats.gender.female)}</span>
                  </div>
                  <div className={styles.legRow}>
                    <span className={styles.legDot} style={{ background: '#D1D5DB' }} />
                    <span className={styles.legLbl}>{t('dashboard_v2.new_customers.other')}</span>
                    <span className={styles.legVal}>{fmt(customerNewStats.gender.other)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.custSplitItem}>
              <div className={styles.custSplitHd}>{t('dashboard_v2.new_customers.phone')}</div>
              <div className={styles.donutBlock}>
                <div
                  className={styles.donutRing}
                  style={{ background: `conic-gradient(${donutPhoneStops})` }}
                >
                  <div className={styles.donutCenter}>
                    <b>{Math.round((customerNewStats.phone.has / (customerNewStats.total || 1)) * 100)}%</b>
                    <span>{t('dashboard_v2.new_customers.has_phone')}</span>
                  </div>
                </div>
                <div className={styles.donutLegend}>
                  <div className={styles.legRow}>
                    <span className={styles.legDot} style={{ background: '#22C55E' }} />
                    <span className={styles.legLbl}>{t('dashboard_v2.new_customers.has_phone')}</span>
                    <span className={styles.legVal}>{fmt(customerNewStats.phone.has)}</span>
                  </div>
                  <div className={styles.legRow}>
                    <span className={styles.legDot} style={{ background: '#D1D5DB' }} />
                    <span className={styles.legLbl}>{t('dashboard_v2.new_customers.no_phone')}</span>
                    <span className={styles.legVal}>{fmt(customerNewStats.phone.none)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== SECTION 4: TOP RANKINGS ===== */}
      <div className={`${styles.section} ${styles.card}`}>
        <div className={styles.cardHd}>
          <div>
            <div className={styles.cardHdTitle}>{t('dashboard_v2.ranking.title')}</div>
            <div className={styles.cardHdSub}>
              {t('dashboard_v2.ranking.sub')}
            </div>
          </div>
          {/* Pill tab buttons */}
          <div className={styles.rankTabGroup}>
            {[
              ['fanpage', t('dashboard_v2.ranking.tab_fanpage')],
              ['tagBombot', t('dashboard_v2.ranking.tab_tag_bombot')],
              ['tagFacebook', t('dashboard_v2.ranking.tab_tag_facebook')],
            ].map(([k, l]) => (
              <button
                key={k}
                className={`${styles.rankTabBtn} ${rankState === k ? styles.rankTabActive : ''}`}
                onClick={() => setRankState(k)}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.cardBd}>
          <div className={styles.rankList}>
            {(() => {
              const data = rankData[rankState] || []
              const maxVal = Math.max(...data.map((d) => d.val), 1)
              return data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF', fontSize: 13 }}>
                  {t('dashboard_v2.ranking.no_data')}
                </div>
              ) : data.map((d, i) => (
                <div key={i} className={styles.rankRow}>
                  <span className={styles.rankIdx}>{i + 1}</span>
                  {/* Dot màu theo rank */}
                  <span
                    className={styles.rankDot}
                    style={{ background: rankColor(i, data.length) }}
                  />
                  <span className={styles.rankLabel} title={d.label}>
                    {d.label}
                  </span>
                  <span className={styles.rankBarWrap}>
                    <span
                      className={styles.rankBarFill}
                      style={{
                        width: `${((d.val / maxVal) * 100).toFixed(1)}%`,
                        background: rankColor(i, data.length),
                        minWidth: d.val === 0 ? 0 : undefined,
                      }}
                    />
                  </span>
                  <span className={`${styles.rankVal} ${i === 0 ? styles.rankValTop : ''}`}>
                    {fmt(d.val)}
                  </span>
                </div>
              ))
            })()}
          </div>
        </div>
      </div>

      {/* ===== SECTION 5: HOẠT ĐỘNG GẦN ĐÂY ===== */}
      <div ref={tableRef} className={`${styles.section} ${styles.grid21}`}>
        {/* CHIẾN DỊCH GẦN ĐÂY */}
        <div className={styles.card}>
          <div className={styles.cardHd}>
            <div className={styles.cardHdTitle}>{t('dashboard_v2.recent_campaigns.title')}</div>
            <Link to={pathKeys.CAMPAIGN} className={styles.linkMore}>
              {t('dashboard_v2.recent_campaigns.view_all')}
              <svg viewBox="0 0 12 12" fill="none">
                <path d="M4.5 2.5l4 3.5-4 3.5" />
              </svg>
            </Link>
          </div>

          <div className={styles.filterRow}>
            {[
              ['all', t('dashboard_v2.recent_campaigns.filter_all')],
              ['sending', t('dashboard_v2.recent_campaigns.filter_sending')],
              ['scheduled', t('dashboard_v2.recent_campaigns.filter_scheduled')],
              ['done', t('dashboard_v2.recent_campaigns.filter_done')],
              ['paused', t('dashboard_v2.recent_campaigns.filter_stopped')],
            ].map(([k, label]) => {
              const count =
                k === 'all'
                  ? campaignList.length
                  : campaignList.filter((c) => c.status === k).length
              return (
                <div
                  key={k}
                  className={`${styles.chip} ${campaignFilter === k ? styles.on : ''}`}
                  onClick={() => setCampaignFilter(k)}
                >
                  {label} <span className={styles.chipCount}>{count}</span>
                </div>
              )
            })}
          </div>

          <div className={styles.tblScroll}>
            <table className={styles.tableV2}>
              <thead>
                <tr>
                  <th>{t('dashboard_v2.recent_campaigns.col_campaign')}</th>
                  <th>{t('dashboard_v2.recent_campaigns.col_fanpage')}</th>
                  <th>{t('dashboard_v2.recent_campaigns.col_status')}</th>
                  <th>{t('dashboard_v2.recent_campaigns.col_time')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '28px 16px', color: '#9CA3AF', fontSize: 13 }}>
                      {t('dashboard_v2.recent_campaigns.empty')}
                    </td>
                  </tr>
                ) : filteredCampaigns.map((c, idx) => {
                  const metaCls = STATUS_META_CLS[c.status] || STATUS_META_CLS.done
                  const statusLabel = {
                    done: t('dashboard_v2.activity.completed'),
                    sending: t('dashboard_v2.activity.running'),
                    scheduled: t('dashboard_v2.activity.scheduled'),
                    failed: t('dashboard_v2.activity.failed'),
                    paused: t('dashboard_v2.activity.stopped'),
                  }[c.status] || t('dashboard_v2.activity.completed')
                  const name = c.name || `${t('update.campaign')} #${c.code}`
                  return (
                    <tr
                      key={idx}
                      className={styles.campaignRow}
                      onClick={() => history.push(`${pathKeys.CAMPAIGN}?code=${c.code}`)}
                    >
                      <td>
                        <div className={styles.campaignCell}>
                          <span className={styles.campaignName}>{name}</span>
                          <span className={styles.campaignCode}>#{c.code}</span>
                        </div>
                      </td>
                      <td>{c.fanpage}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${metaCls}`}>
                          <span className={styles.statusDot} />
                          {statusLabel}
                        </span>
                      </td>
                      <td>
                        <div className={styles.timeCell}>
                          <span className={styles.timeMain}>{c.rel}</span>
                        </div>
                      </td>
                      <td>
                        <svg className={styles.chevron} viewBox="0 0 14 14" fill="none">
                          <path d="M5 2.5l5 4.5-5 4.5" stroke="currentColor" />
                        </svg>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* KỊCH BẢN SẮP GỬI */}
        <div className={styles.card}>
          <div className={styles.cardHd}>
            <div className={styles.cardHdTitle}>{t('dashboard_v2.scenarios.title')}</div>
          </div>
          <div className={styles.cardBd} style={{ padding: 0 }}>
            {(() => {
              const list = upcomingScenarios?.content || upcomingScenarios || []
              if (!list.length) {
                return (
                  <div className={styles.tblEmpty}>
                    <div className={styles.tblEmptyIcon}>
                      <svg viewBox="0 0 20 20" fill="none">
                        <rect x="3" y="4" width="14" height="13" rx="1.5" stroke="currentColor" />
                        <path d="M6.5 4V2.5M13.5 4V2.5M3 8h14" stroke="currentColor" />
                      </svg>
                    </div>
                    <div className={styles.tblEmptyTitle}>{t('dashboard_v2.scenarios.empty_title')}</div>
                    <div className={styles.tblEmptySub}>
                      {t('dashboard_v2.scenarios.empty_sub')}
                    </div>
                    <button
                      className={styles.btnCreateSm}
                      onClick={() => window.open(
                        types.URL.FACEBOOK + `&token=${localStorage.getItem('token') || null}`,
                        '_blank'
                      )}
                    >
                      <svg viewBox="0 0 14 14" fill="none">
                        <path d="M7 2v10M2 7h10" stroke="#fff" strokeWidth="2.2" />
                      </svg>
                      {t('dashboard_v2.scenarios.create_campaign')}
                    </button>
                  </div>
                )
              }
              return (
                <div className={styles.tblScroll}>
                  <table className={styles.tableV2}>
                    <thead>
                      <tr>
                        <th>{t('dashboard_v2.scenarios.col_scenario')}</th>
                        <th>{t('dashboard_v2.scenarios.col_campaign')}</th>
                        <th>{t('dashboard_v2.scenarios.col_time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((s, idx) => (
                        <tr
                          key={idx}
                          className={styles.campaignRow}
                          onClick={() => history.push(`${pathKeys.CAMPAIGN}?code=${s.campaignCode || s.campaignId || ''}`)}
                        >
                          <td>
                            <div className={styles.campaignCell}>
                              <span className={styles.campaignName}>{s.name || s.title || `${t('dashboard_v2.scenarios.col_scenario')} #${idx + 1}`}</span>
                              {s.code && <span className={styles.campaignCode}>#{s.code}</span>}
                            </div>
                          </td>
                          <td>{s.campaignName || s.campaignCode || '—'}</td>
                          <td>
                            <div className={styles.timeCell}>
                              <span className={styles.timeMain}>
                                {s.scheduledAt
                                  ? moment(s.scheduledAt).format('DD/MM/YYYY HH:mm')
                                  : s.createdAt
                                    ? moment(s.createdAt).fromNow()
                                    : '—'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardV2
